import { CustomerUiProvider } from "@babackapp/customer-ui/components/provider";
import { TooltipProvider } from "@babackapp/customer-ui/components/tooltip";
import {
  configureMerchantToken,
  configureReturnWidgetBaseUrl,
} from "@babackapp/private-api-client/client/return-widget-client";
import { usePublicShopSuspense } from "@babackapp/private-api-client/client/use-shop-suspense";
import { tokenManager } from "@babackapp/auth-client/utils";
import type { EmbedToWidgetMessage } from "@babackapp/return-widget-components/types/shop-now";
import { createStore, Provider } from "jotai";
import { AnimatePresence, motion } from "motion/react";
import { type ReactNode, Suspense, useEffect, useState } from "react";
import { useLocation, useOutlet, useParams } from "react-router-dom";
import { WidgetErrorBoundary } from "./widget-error-boundary";
import { WidgetLoading } from "./widget-loading";
import { WidgetUnavailable } from "./widget-unavailable";
import { useWidgetUnavailableReason } from "../hooks/use-widget-unavailable-reason";

// Configure the return widget API client to use the same API URL and merchant
// token as the rest of baback-office, so CORS and auth work correctly.
configureReturnWidgetBaseUrl(import.meta.env.VITE_API_URL ?? "https://api.baback.co/");
configureMerchantToken(() => tokenManager.getAccessToken());

const widgetStore = createStore();

const API_BASE = (import.meta.env.VITE_API_URL ?? "https://api.baback.co/").replace(/\/$/, "");

export function ReturnWidgetLayout() {
  const segment = useLocation().pathname.split("/")[3];
  const outlet = useOutlet();

  return (
    <Provider store={widgetStore}>
      <TooltipProvider>
        <WidgetErrorBoundary>
          <Suspense fallback={<WidgetLoading />}>
            <AvailabilityGate>
              <ThemedLayout segment={segment} outlet={outlet} />
            </AvailabilityGate>
          </Suspense>
        </WidgetErrorBoundary>
      </TooltipProvider>
    </Provider>
  );
}

// Covers the cases the Suspense fallback cannot: a 5xx or an outage detected once the
// flow is already rendered, and a maintenance flag flipped mid-session.
function AvailabilityGate({ children }: { children: ReactNode }) {
  const reason = useWidgetUnavailableReason();

  if (!reason) return children;

  return (
    <div className="flex h-screen w-screen flex-col bg-white">
      <WidgetUnavailable reason={reason} />
    </div>
  );
}

function ThemedLayout({
  segment,
  outlet,
}: {
  segment: string;
  outlet: ReactNode;
}) {
  const { shopId } = useParams<{ shopId: string }>();
  // craftsman-ignore: TS003 — :shopId is a required segment of the only route
  // that renders this layout, so it is never undefined here.
  const { data: shop } = usePublicShopSuspense(shopId!);
  const [parentFont, setParentFont] = useState<string | undefined>();

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const msg = event.data as EmbedToWidgetMessage;
      if (msg?.type !== "WIDGET_FONT") return;

      if (msg.fontFaceUrl) {
        const face = new FontFace(msg.fontFamily, `url(${msg.fontFaceUrl})`);
        face.load().then((f) => document.fonts.add(f)).catch(() => {});
      }
      setParentFont(msg.fontFamily);
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const primary = shop?.preferences?.customisation?.primary_600;
  const customCssUrl = shop?.preferences?.customCss
    ? `${API_BASE}/public/custom-css/${shopId}.css`
    : undefined;

  return (
    <CustomerUiProvider
      className="h-screen w-screen bg-white flex flex-col overflow-hidden"
      colors={primary ? { primary } : undefined}
      fonts={parentFont ? { sans: parentFont } : undefined}
      customCssUrl={customCssUrl}
    >
      <div className="relative flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0 z-0 relative overflow-y-auto flex flex-col p-8 pb-16">
          <WidgetErrorBoundary>
            <Suspense>
              <AnimatePresence mode="wait" propagate>
                <motion.div key={segment} className="flex-1 min-h-0 w-full flex flex-col">
                  {outlet}
                </motion.div>
              </AnimatePresence>
            </Suspense>
          </WidgetErrorBoundary>
        </div>
      </div>
    </CustomerUiProvider>
  );
}
